import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'; 
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'react-qr-code';
import { styled } from '@mui/material/styles';

const AppCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
  },
}));

const Apps = () => {
  const [selectedApp, setSelectedApp] = React.useState(null);

  const apps = [
    {
      id: 1,
      title: 'Sudoku Journey',
      description: 'Sudoku Journey: Crypto Grandpa\'s Trip Down Memory',
      longDescription: `Sudoku Journey: Coin Grandpa is more than a puzzle game. 
        Enjoy classic, daily, and story modes – with episodes full of light humor and
        relaxing vibes.`,
      image: '/images/sudoku_icon.png', // Replace with actual app image
      playStoreLink: 'https://play.google.com/store/apps/details?id=com.kkk.sudokujourney',
      screenshots: [
        '/images/sudoku_01.png',
        '/images/sudoku_02.png',
        '/images/sudoku_03.png',
      ],
      features: [
        'Classic, Daily, and Story Sudoku',
        'Mind & wellness tips included',
        'Season Story: The crypto journey of “Coin Grandpa”',
      ],
    },
    {
      id: 2,
      title: 'Focus Timer',
      description: 'Focus timer and to-do planner for smarter study sessions.',
      longDescription: `Want to study smarter, not harder? Meet Focusly. 
        Focusly is the ultimate productivity app for students who want to stay
        focused, organized, and motivated.`,
      image: '/images/timer_icon.png', // Replace with actual app image
      playStoreLink: 'https://play.google.com/store/apps/details?id=com.kkk.focustrack_timer',
      screenshots: [
        '/images/timer_01.PNG',
        '/images/timer_02.PNG',
        '/images/timer_03.PNG',
      ],
      features: [
        'Pomodoro-based focus timer',
        'Task management with a smart to-do list',
        'Visual daily study planner',
      ],
    },
    {
      id: 3,
      title: 'NextBallAI',
      description: 'Get the latest Powerball results, AI predictions & smart alerts with NextBallAI!',
      longDescription: `NextBallAI is your ultimate Powerball companion! 
        Stay ahead of the game with real-time Powerball winning numbers, AI-
        powered number predictions, draw statistics, and personalized draw alerts – 
        all in one sleek, easy-to-use app designed for U.S. lottery players.`,
      image: '/images/nextball_icon.png', // Replace with actual app image
      playStoreLink: 'https://play.google.com/store/apps/details?id=com.kdev.k.nextballai',
      screenshots: [
        '/images/nextball_01.png',
        '/images/nextball_02.png',
        '/images/nextball_03.png',
      ],
      features: [
        'Real-time Powerball results',
        'AI-powered number predictions',
        'Draw statistics & trends',
        'Personalized draw alerts',
        'Easy-to-use interface',
        'No in-app purchases',
        'No ads',
        'No login required',
        'No data collection',
      ],
    },
    {
      id: 4,
      title: '로또지니AI',
      description: '로또 당첨 번호 통계와 AI 예측으로 당첨에 한 걸음 더 가까이!',
      longDescription: `로또지니AI – 똑똑한 로또 도우미!
        QR코드인식을 통한 스마트한 결과 확인,
        매주 로또 추첨 전 알림,
        로또지니AI는 로또 번호를 단순히 보여주는 앱이 아닙니다.
        당첨 번호 통계와 AI 기술을 활용해, 더 나은 선택을 돕는 스마트한 로또 분석 앱입니다.`,
      image: '/images/genielotto_icon.png', // Replace with actual app image
      playStoreLink: 'https://play.google.com/store/apps/details?id=com.kdev.k.genielottoai',
      screenshots: [
        '/images/genielotto_01.png',
        '/images/genielotto_02.png',
        '/images/genielotto_03.png',
        '/images/genielotto_04.png',
      ],
      features: [
        'QR코드 인식을 통한 스마트한 결과 확인',
        '매주 로또 추첨 전 알림',
        'AI 기술을 활용한 번호 분석',
        '당첨 번호 통계 및 추이 분석',
        '사용자 친화적인 인터페이스',
      ],
    },
  ];

  const handleOpenDialog = (app) => {
    setSelectedApp(app);
  };

  const handleCloseDialog = () => {
    setSelectedApp(null);
  };

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h1" align="center" gutterBottom>
          Our Apps
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Discover our innovative mobile applications
        </Typography>

        <Grid container spacing={4} sx={{ mt: 2 }}>
          {apps.map((app) => (
            <Grid item key={app.id} xs={12} sm={6} md={4}>
              <AppCard>
                <CardMedia
                  component="img"
                  height="200"
                  image={app.image}
                  alt={app.title}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {app.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {app.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(app)}
                  >
                    Learn More
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    href={app.playStoreLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get it on Google Play
                  </Button>
                </CardActions>
              </AppCard>
            </Grid>
          ))}
        </Grid>

        {/* App Detail Dialog */}
        <Dialog
          open={Boolean(selectedApp)}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedApp && (
            <>
              <DialogTitle>
                {selectedApp.title}
                <IconButton
                  aria-label="close"
                  onClick={handleCloseDialog}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box
                      component="img"
                      src={selectedApp.image}
                      alt={selectedApp.title}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 1,
                      }}
                    />
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <QRCode
                        value={selectedApp.playStoreLink}
                        size={128}
                        style={{ margin: '0 auto' }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Scan to download
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      About the App
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {selectedApp.longDescription}
                    </Typography>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Key Features
                    </Typography>
                    <ul>
                      {selectedApp.features.map((feature, index) => (
                        <li key={index}>
                          <Typography variant="body1">{feature}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Screenshots
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedApp.screenshots.map((screenshot, index) => (
                        <Grid item xs={12} sm={4} key={index}>
                          <Box
                            component="img"
                            src={screenshot}
                            alt={`Screenshot ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: 1,
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  href={selectedApp.playStoreLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="primary"
                >
                  Download on Google Play
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default Apps; 